import { CheckCircle2, Clock, CreditCard, FileText, MapPin, Monitor, Settings, Users, Utensils } from 'lucide-react';

export const STATUS_CONFIG: Record<string, { bg: string; text: string; label: string }> = {
  PAID: { bg: 'bg-emerald-50', text: 'text-emerald-700', label: 'Payée' },
  CONFIRMED: { bg: 'bg-sky-50', text: 'text-sky-700', label: 'Confirmée' },
  PENDING: { bg: 'bg-amber-50', text: 'text-amber-700', label: 'En attente' },
  CANCELLED: { bg: 'bg-rose-50', text: 'text-rose-700', label: 'Annulée' },
  QUOTE_REQUESTED: { bg: 'bg-violet-50', text: 'text-violet-700', label: 'Devis demandé' },
};

export const PAYMENT_CONFIG: Record<string, { bg: string; text: string; label: string }> = {
  PAID: { bg: 'bg-emerald-50', text: 'text-emerald-700', label: 'Payé' },
  PENDING: { bg: 'bg-amber-50', text: 'text-amber-700', label: 'En attente' },
  FAILED: { bg: 'bg-rose-50', text: 'text-rose-700', label: 'Échec' },
  REFUNDED: { bg: 'bg-slate-100', text: 'text-slate-800', label: 'Remboursé' },
};

export const DOC_TYPE_CONFIG: Record<string, { bg: string; text: string; icon: typeof FileText }> = {
  invoice: { bg: 'bg-emerald-50', text: 'text-emerald-700', icon: CreditCard },
  quote: { bg: 'bg-violet-50', text: 'text-violet-700', icon: FileText },
  confirmation: { bg: 'bg-sky-50', text: 'text-sky-700', icon: CheckCircle2 },
  contract: { bg: 'bg-amber-50', text: 'text-amber-700', icon: FileText },
  id: { bg: 'bg-slate-50', text: 'text-slate-700', icon: FileText },
  purchase_order: { bg: 'bg-indigo-50', text: 'text-indigo-700', icon: FileText },
};

/**
 * Mapping des clés de documents vers leurs labels français
 */
export const DOCUMENT_KEY_LABELS: Record<string, string> = {
  // Documents d'identité (majuscules et minuscules)
  LEGAL_REP_ID: "Pièce d'identité du représentant légal",
  legal_rep_id: "Pièce d'identité du représentant légal",
  id_card: "Pièce d'identité",
  identity: "Pièce d'identité",
  passport: 'Passeport',

  // Documents d'entreprise
  COMPANY_REGISTRATION: 'Immatriculation entreprise',
  company_registration: 'Immatriculation entreprise',
  business_license: 'Registre de commerce',
  kbis: 'Extrait Kbis',

  // Documents financiers
  FINANCIAL_STATEMENTS: 'États financiers',
  financial_statements: 'États financiers',
  CIRCUIT_FORM: 'Fiche Circuit',
  circuit_form: 'Fiche Circuit',
  tax_certificate: 'Attestation fiscale',
  rib: 'RIB',
  bank: 'Coordonnées bancaires',

  // Documents administratifs
  OCCUPANTS_LIST: 'Liste des occupants',
  occupants_list: 'Liste des occupants',
  PARTICIPANTS_LIST: 'Liste des participants',
  participants_list: 'Liste des participants',
  proof_of_address: 'Justificatif de domicile',
  insurance: "Attestation d'assurance",
  event_program: "Programme de l'événement",

  // Documents de réservation
  BOOKING_CONFIRMATION: 'Confirmation de réservation',
  booking_confirmation: 'Confirmation de réservation',
  QUOTE: 'Devis',
  quote: 'Devis',
  INVOICE: 'Facture',
  invoice: 'Facture',
  CONTRACT: 'Contrat',
  contract: 'Contrat',
  purchase_order: 'Bon de commande',

  // Autres
  OTHER: 'Autre document',
  other: 'Autre document',
  ADDITIONAL_DOC: 'Document complémentaire',
  additional_doc: 'Document complémentaire',
};

/**
 * Retourne le label français d'une clé de document
 * @param documentKey - La clé du document (ex: CIRCUIT_FORM, id_card)
 * @param adminLabels - Labels personnalisés configurés par l'admin (optionnel)
 * @returns Le label français du document
 *
 * Priorité : adminLabels > DOCUMENT_KEY_LABELS > fallback formaté
 */
export function getDocumentLabel(documentKey: string, adminLabels?: Record<string, string>): string {
  // 1. Priorité aux labels admin si fournis
  if (adminLabels && adminLabels[documentKey]) {
    return adminLabels[documentKey];
  }

  // 2. Labels par défaut
  if (DOCUMENT_KEY_LABELS[documentKey]) {
    return DOCUMENT_KEY_LABELS[documentKey];
  }

  // 3. Fallback: formater la clé (CIRCUIT_FORM → Circuit Form)
  return documentKey
    .split('_')
    .map((word) => word.charAt(0) + word.slice(1).toLowerCase())
    .join(' ');
}

export const SERVICE_CONFIG: Record<string, { icon: typeof Monitor; bg: string; text: string }> = {
  equipment: { icon: Monitor, bg: 'bg-blue-50', text: 'text-blue-700' },
  catering: { icon: Utensils, bg: 'bg-orange-50', text: 'text-orange-700' },
  support: { icon: Users, bg: 'bg-green-50', text: 'text-green-700' },
  setup: { icon: Settings, bg: 'bg-purple-50', text: 'text-purple-700' },
};

export const LAYOUT_CONFIG: Record<string, string> = {
  theatre: 'Théâtre',
  'u-shape': 'En U',
  cocktail: 'Cocktail',
  meeting: 'Réunion',
  classroom: 'Classe',
};

export const TIMELINE_TYPE_CONFIG: Record<string, { color: string; dotColor: string }> = {
  info: { color: 'text-sky-700', dotColor: 'bg-sky-500' },
  success: { color: 'text-emerald-700', dotColor: 'bg-emerald-500' },
  warning: { color: 'text-amber-700', dotColor: 'bg-amber-500' },
  error: { color: 'text-rose-700', dotColor: 'bg-rose-500' },
};

export const DRAWER_TABS = [
  { id: 'details' as const, label: 'Détails', icon: MapPin },
  { id: 'timeline' as const, label: 'Historique', icon: Clock },
  { id: 'documents' as const, label: 'Documents', icon: FileText },
];
